import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import "./CreateEditOrganizerPage.css"; // Asegúrate de que este archivo CSS exista

function CreateEditOrganizerPage() {
  const [organizerName, setOrganizerName] = useState("");
  const [organizerLogoFile, setOrganizerLogoFile] = useState(null);
  const [useDefaultLogo, setUseDefaultLogo] = useState(false);
  const [currentLogoUrl, setCurrentLogoUrl] = useState(""); // Para mostrar el logo actual en modo edición
  const [initialLogoUrl, setInitialLogoUrl] = useState(""); // Guarda la URL original para detectar cambios

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successPopupMessage, setSuccessPopupMessage] = useState("");

  const { id: organizerId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // useEffect: Carga datos del organizador si es modo edición
  useEffect(() => {
    const fetchOrganizerData = async () => {
      if (!organizerId) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError("");
      try {
        const response = await axios.get(
          `${API_BASE_URL}/organizers/${organizerId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = response.data;
        setOrganizerName(data.name);
        setCurrentLogoUrl(data.url_logo || ""); // Guarda la URL del logo actual
        setInitialLogoUrl(data.url_logo || ""); // Guarda la URL inicial para comparación
      } catch (err) {
        setError(
          err.response?.data?.error ||
            "Error al cargar los datos del organizador."
        );
        console.error("Error fetching organizer data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchOrganizerData();
    }
  }, [organizerId, token, API_BASE_URL]);

  const handleFileChange = (event) => {
    setOrganizerLogoFile(event.target.files[0]);
    setUseDefaultLogo(false); // Si se sube un archivo, desactiva el uso de logo por defecto
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setError("");
    setMessage("");
    setSuccessPopupMessage("");
    setShowSuccessPopup(false);

    if (!organizerName.trim()) {
      setError("El nombre del organizador es requerido.");
      setIsSaving(false);
      return;
    }

    const formData = new FormData();
    formData.append("name", organizerName.trim()); // Siempre envía el nombre

    // Lógica de logo para FormData
    if (organizerLogoFile) {
      // Se seleccionó un nuevo archivo
      formData.append("logo", organizerLogoFile);
    } else if (useDefaultLogo) {
      // Se marcó usar logo por defecto
      formData.append("useDefaultLogo", "true");
    } else if (organizerId) {
      // Si estamos editando y no hay nuevo archivo ni se usó por defecto
      if (initialLogoUrl && !currentLogoUrl) {
        // Si el logo inicial existía y ahora se vació (ej. se borró visualmente o no se marcó nada)
        formData.append("clearLogo", "true"); // Indica al backend que borre el logo existente
      } else if (!initialLogoUrl && !currentLogoUrl) {
        // Si nunca hubo logo y sigue sin haberlo (no se necesita acción)
      }
      // Si currentLogoUrl tiene un valor (no se cambió el logo y no es nulo), no se añade nada al FormData
      // El backend mantendrá el logo existente por defecto si no recibe 'logo', 'useDefaultLogo', o 'clearLogo'.
    } else {
      // Creando nuevo organizador sin archivo y sin usar default.
      // Para la creación, si no hay logoFile y no se usa default, es un error
      if (!organizerLogoFile && !useDefaultLogo) {
        setError(
          "Debe seleccionar un archivo de logo o usar el logo por defecto para el nuevo organizador."
        );
        setIsSaving(false);
        return;
      }
    }

    try {
      let response;
      if (organizerId) {
        // Modo edición (PUT)
        response = await axios.put(
          `${API_BASE_URL}/organizers/${organizerId}`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSuccessPopupMessage("Organizador actualizado exitosamente.");
      } else {
        // Modo creación (POST)
        response = await axios.post(`${API_BASE_URL}/organizers`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccessPopupMessage("Organizador creado exitosamente.");
        // Limpiar formulario solo al crear
        setOrganizerName("");
        setOrganizerLogoFile(null);
        setUseDefaultLogo(false);
        setCurrentLogoUrl(""); // Limpiar previsualización
        setInitialLogoUrl(""); // Limpiar inicial
      }
      console.log(
        "Respuesta del backend (guardar organizador):",
        response.data
      );
      setShowSuccessPopup(true);
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar el organizador.");
      console.error(
        "Error saving organizer:",
        err.response?.data || err.message
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleClosePopup = () => {
    setShowSuccessPopup(false);
    navigate("/admin/organizers");
  };

  if (isLoading) {
    return (
      <div className="create-edit-organizer-container">
        <p>Cargando datos...</p>
      </div>
    );
  }

  return (
    <div className="create-edit-organizer-container">
      <Link to="/admin/organizers" className="back-button">
        <span className="material-icons">arrow_back</span>
      </Link>

      <div className="create-edit-organizer-card">
        <h2 className="create-edit-organizer-title">
          {organizerId ? "Editar Organizador" : "Crear Nuevo Organizador"}
        </h2>
        <p className="create-edit-organizer-description">
          {organizerId
            ? "Modifica la información del organizador existente."
            : "Registra un nuevo organizador para tus eventos."}
        </p>

        {message && (
          <p className="create-edit-organizer-message success">{message}</p>
        )}
        {error && (
          <p className="create-edit-organizer-message error">{error}</p>
        )}

        <form onSubmit={handleSubmit} className="create-edit-organizer-form">
          <div className="form-section">
            <h3>Detalles del Organizador</h3>
            <div className="form-group">
              <label htmlFor="organizerName">Nombre del Organizador:</label>
              <input
                type="text"
                id="organizerName"
                value={organizerName}
                onChange={(e) => setOrganizerName(e.target.value)}
                className="organizer-input"
                disabled={isSaving}
              />
            </div>

            {/* Sección de Logo */}
            <div className="form-group">
              <label>Logo del Organizador:</label>
              {/* Previsualización del logo actual si existe y no se ha seleccionado uno nuevo ni se usa por defecto */}
              {organizerId &&
                (initialLogoUrl || currentLogoUrl) &&
                !organizerLogoFile &&
                !useDefaultLogo && (
                  <div className="current-logo-preview">
                    <img
                      src={`${API_BASE_URL}/uploads/${
                        currentLogoUrl || initialLogoUrl
                      }`}
                      alt={`Logo actual de ${organizerName}`}
                      className="organizer-logo-thumbnail"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src =
                          "https://placehold.co/60x60/cccccc/333333?text=Logo";
                      }}
                    />
                    <span>Logo actual</span>
                    {/* Opción para borrar el logo actual si se está editando y hay un logo */}
                    {currentLogoUrl && ( // Solo mostrar el botón de borrar si hay un logo que borrar
                      <button
                        type="button"
                        onClick={() =>
                          setCurrentLogoUrl("")
                        } /* Vacía la URL para indicar borrado */
                        className="clear-logo-button"
                        title="Eliminar logo actual"
                      >
                        <span className="material-icons">close</span>
                      </button>
                    )}
                  </div>
                )}
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="organizer-input-file"
                disabled={isSaving || useDefaultLogo}
              />
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="useDefaultLogo"
                  checked={useDefaultLogo}
                  onChange={(e) => {
                    setUseDefaultLogo(e.target.checked);
                    if (e.target.checked) setOrganizerLogoFile(null);
                  }}
                  disabled={isSaving}
                />
                <label htmlFor="useDefaultLogo">
                  Usar logo por defecto (ministerio_logo.png)
                </label>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSaving}
            className="create-edit-organizer-submit-button"
          >
            {isSaving
              ? "Guardando..."
              : organizerId
              ? "Actualizar Organizador"
              : "Crear Organizador"}
          </button>
        </form>
      </div>

      {/* Pop-up de éxito */}
      {showSuccessPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>¡Éxito!</h3>
            <p>{successPopupMessage}</p>
            <button onClick={handleClosePopup} className="popup-button">
              Cerrar y Volver a Organizadores
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateEditOrganizerPage;
